<template>
  <section class="schedule-page">
    <TabsSwitchBar
      v-model="section"
      :tabs="tabs"
      :role-class="roleClass"
      :mobile-equal="true"
    />

    <div class="schedule-page__content">
      <QueryErrorNotice :error="schedulePageErrors" />

      <template v-if="section === 'day'">
        <DataSlider
          type="week"
          :items="dayItems"
          :model-value="selectedDay"
          :border-bottom="true"
          @change="onDayChange"
        />
        <ScheduleDayTab
          :loading="isScheduleLoading"
          :day-item="selectedDay"
          :lessons="selectedDayLessons"
          :lang="lang"
          :schedule-types="scheduleTypes"
          :schedule-type="scheduleType"
          @change-schedule-type="changeScheduleType"
          @open-lesson="openLesson"
        />
      </template>

      <template v-else-if="section === 'week'">
        <DataSlider
          type="month"
          :items="weekItems"
          :month="months.source"
          :model-value="selectedWeek"
          @change="onWeekChange"
        />
        <ScheduleWeekTab
          :loading="isScheduleLoading"
          :week-item="selectedWeek"
          :week-days="weekDays"
          :lang="lang"
          :schedule-types="scheduleTypes"
          :schedule-type="scheduleType"
          @change-schedule-type="changeScheduleType"
          @go-day="goToDay"
        />
      </template>

      <template v-else>
        <ScheduleBellTab :loading="isBellLoading" :bells="bellList" />
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import DataSlider from "@/components/common/DataSlider.vue";
import QueryErrorNotice from "@/components/common/QueryErrorNotice.vue";
import TabsSwitchBar from "@/components/common/TabsSwitchBar.vue";
import ScheduleBellTab from "@/components/schedule/ScheduleBellTab.vue";
import ScheduleDayTab from "@/components/schedule/ScheduleDayTab.vue";
import ScheduleWeekTab from "@/components/schedule/ScheduleWeekTab.vue";
import {
  usePeriodsQuery,
  useScheduleBellsQuery,
  useScheduleMarksQuery,
  useSchedulePlansQuery,
  useScheduleRaspisanieQuery,
} from "@/queries";
import { useLoadingStore } from "@/stores/loading";
import { useTopbarStore } from "@/stores/topbar";
import { useViewerStore } from "@/stores/viewer";
import {
  buildPeriodSliderData,
  toPeriodDateKey,
  toUtcDaySeconds,
} from "@/utils/periodSlider";
import { normalizeDateKey, normalizeDayLessons, rawRow } from "@/utils/schedule";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const viewerStore = useViewerStore();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
viewerStore.initFromStorage();

const section = ref("day");
const scheduleType = ref(0);

const selectedChild = computed(() => viewerStore.selectedChild || null);
const roleClass = computed(() =>
  viewerStore.isParent ? "active-parent" : "active-pupil"
);
const lang = computed(() => (localStorage.getItem("translation") === "kz" ? "kz" : "ru"));
const locale = computed(() => (lang.value === "kz" ? "kk-KZ" : "ru-RU"));

const pupilId = computed(() =>
  Number(selectedChild.value?.pupil_id || viewerStore.profile?.pupil_id || 0)
);
const groupId = computed(() =>
  Number(selectedChild.value?.group_id || viewerStore.profile?.group_id || 0)
);

const tabs = computed(() => [
  { value: "day", label: t("day_button_label") },
  { value: "week", label: t("month_button_label") },
  { value: "bell", label: t("bell_button_label") },
]);

const periodsQuery = usePeriodsQuery({}, { staleTime: 5 * 60 * 1000, retry: false });
const sliderData = computed(() =>
  buildPeriodSliderData(periodsQuery.data.value, {
    locale: locale.value,
    selectedTs: Date.now(),
  })
);

const days = computed(() => sliderData.value.days || { items: [], current: null });
const weeks = computed(() => sliderData.value.weeks || { items: [], current: null });
const months = computed(() => sliderData.value.months);
const dayItems = computed(() =>
  Array.isArray(days.value.items) ? days.value.items : []
);
const weekItems = computed(() =>
  Array.isArray(weeks.value.items) ? weeks.value.items : []
);

const selectedDay = ref(null);
const selectedWeek = ref(null);

watch(
  () => dayItems.value,
  (items) => {
    if (!items.length) {
      selectedDay.value = null;
      return;
    }
    const currentTs = Number(selectedDay.value?.day?.date || 0);
    const found = items.find((item) => Number(item?.day?.date || 0) === currentTs);
    selectedDay.value = found || days.value.current || items[0];
  },
  { immediate: true }
);

function normalizeRouteDayMs(value) {
  const raw = Number(value || 0);
  if (!raw || Number.isNaN(raw)) return 0;
  if (raw < 1e12) return raw * 1000;
  return raw;
}

function applyRouteDay(dayMs) {
  if (!dayMs || !dayItems.value.length) return false;
  const found = dayItems.value.find(
    (item) => Number(item?.day?.date || 0) === Number(dayMs || 0)
  );
  if (!found) return false;
  selectedDay.value = found;
  nextTick(() => {
    onDayChange(found);
  });
  return true;
}

watch(
  () => [route.query.day, dayItems.value.length],
  ([queryDay]) => {
    const dayMs = normalizeRouteDayMs(queryDay);
    if (!dayMs) return;
    applyRouteDay(dayMs);
  },
  { immediate: true }
);

watch(
  () => weekItems.value,
  (items) => {
    if (!items.length) {
      selectedWeek.value = null;
      return;
    }
    const currentStart = Number(selectedWeek.value?.week?.start || 0);
    const found = items.find((item) => Number(item?.week?.start || 0) === currentStart);
    selectedWeek.value = found || weeks.value.current || items[0];
  },
  { immediate: true }
);

const selectedDayTs = computed(() =>
  toUtcDaySeconds(selectedDay.value?.day?.date)
);
const requestedDayTs = ref(0);
const selectedWeekRange = computed(() => {
  const start = toUtcDaySeconds(selectedWeek.value?.week?.start);
  const endRaw = toUtcDaySeconds(selectedWeek.value?.week?.end);
  return {
    start,
    end: endRaw > 0 ? endRaw - 1 : 0,
  };
});
const requestedWeekStartTs = ref(0);
const requestedWeekEndTs = ref(0);

const scheduleQuery = useScheduleRaspisanieQuery(requestedDayTs, groupId, pupilId, {
  retry: false,
  placeholderData: (prev) => prev,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});
const schedulePlansQuery = useSchedulePlansQuery(
  requestedWeekStartTs,
  requestedWeekEndTs,
  groupId,
  {
    retry: false,
    placeholderData: (prev) => prev,
  }
);
const scheduleMarksQuery = useScheduleMarksQuery(
  requestedWeekStartTs,
  requestedWeekEndTs,
  pupilId,
  {
    retry: false,
    placeholderData: (prev) => prev,
  }
);
const bellsQuery = useScheduleBellsQuery({
  retry: false,
  staleTime: 5 * 60 * 1000,
});

const cachedScheduleTypes = ref([]);

function toScheduleType(item) {
  if (!item || typeof item !== "object") return null;
  const type = Number(item.type || 0);
  if (!type) return null;
  return {
    ...item,
    type,
    data: item.data && typeof item.data === "object" ? item.data : {},
  };
}

function mergeScheduleTypes(list) {
  const nextList = Array.isArray(list) ? list : [];
  if (!nextList.length) return;

  const merged = [...cachedScheduleTypes.value];
  nextList.forEach((raw) => {
    const item = toScheduleType(raw);
    if (!item) return;
    const idx = merged.findIndex((entry) => Number(entry?.type || 0) === item.type);
    if (idx === -1) {
      merged.push(item);
      return;
    }
    const prev = merged[idx] || {};
    merged[idx] = {
      ...prev,
      ...item,
      data: {
        ...(prev?.data && typeof prev.data === "object" ? prev.data : {}),
        ...item.data,
      },
    };
  });
  cachedScheduleTypes.value = merged;
}

const scheduleTypes = computed(() => cachedScheduleTypes.value);

watch(
  () => scheduleQuery.data.value,
  (value) => {
    const list = Array.isArray(value) ? value : [];
    mergeScheduleTypes(list);
  },
  { immediate: true }
);

watch(
  () => [Number(groupId.value || 0), Number(pupilId.value || 0)],
  () => {
    cachedScheduleTypes.value = [];
    requestedDayTs.value = Number(selectedDayTs.value || 0);
    requestedWeekStartTs.value = Number(selectedWeekRange.value.start || 0);
    requestedWeekEndTs.value = Number(selectedWeekRange.value.end || 0);
  },
  { immediate: true }
);

watch(
  () => Number(selectedDayTs.value || 0),
  (ts) => {
    if (!ts) return;
    if (!Number(requestedDayTs.value || 0)) {
      requestedDayTs.value = ts;
    }
  },
  { immediate: true }
);

watch(
  () => [
    Number(selectedWeekRange.value.start || 0),
    Number(selectedWeekRange.value.end || 0),
  ],
  ([start, end]) => {
    if (!start || !end) return;
    if (!requestedWeekStartTs.value || !requestedWeekEndTs.value) {
      requestedWeekStartTs.value = start;
      requestedWeekEndTs.value = end;
    }
  },
  { immediate: true }
);

watch(
  () => scheduleTypes.value,
  (list) => {
    if (!list.length) {
      scheduleType.value = 0;
      return;
    }
    const exists = list.some(
      (item) => Number(item.type || 0) === Number(scheduleType.value || 0)
    );
    if (!exists) {
      scheduleType.value = Number(list[0].type || 0);
    }
  },
  { immediate: true }
);

const selectedSchedule = computed(() => {
  const list = scheduleTypes.value;
  if (!list.length) return null;
  return (
    list.find((item) => Number(item.type || 0) === Number(scheduleType.value || 0)) ||
    list[0]
  );
});
const selectedScheduleTitle = computed(() => {
  const title = selectedSchedule.value?.title;
  if (!title) return t("child-schedule");
  if (typeof title === "string") return title;
  return (
    title?.[lang.value] ||
    title?.[lang.value === "kz" ? "kk" : "ru"] ||
    title?.ru ||
    title?.kk ||
    title?.kz ||
    t("child-schedule")
  );
});

const selectedScheduleData = computed(() => {
  const data = selectedSchedule.value?.data;
  return data && typeof data === "object" ? data : {};
});

const mergedScheduleData = computed(() =>
  mergeScheduleData(
    selectedScheduleData.value,
    Array.isArray(schedulePlansQuery.data.value) ? schedulePlansQuery.data.value : [],
    Array.isArray(scheduleMarksQuery.data.value) ? scheduleMarksQuery.data.value : []
  )
);

const selectedDayKey = computed(() =>
  toPeriodDateKey(Number(selectedDay.value?.day?.date || Date.now()))
);

const selectedDayLessons = computed(() =>
  normalizeDayLessons(mergedScheduleData.value[selectedDayKey.value] || [])
);

const weekDays = computed(() => {
  const currentWeek = selectedWeek.value?.week;
  const weekStartTs = Number(currentWeek?.start || 0);
  if (!weekStartTs) return [];

  const parseDateTs = (value) => {
    if (!value) return 0;
    const s = String(value).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
    }
    const ts = new Date(s).getTime();
    return Number.isFinite(ts) ? ts : 0;
  };

  const holidayRanges = Array.isArray(sliderData.value?.parsed?.holidays)
    ? sliderData.value.parsed.holidays
        .map((item) => ({
          start: parseDateTs(item?.start),
          end: parseDateTs(item?.end),
        }))
        .filter((item) => item.start > 0 && item.end > item.start)
    : [];

  const isVacationDay = (ts) =>
    holidayRanges.some((range) => ts >= range.start && ts < range.end);

  const DAY_MS = 24 * 60 * 60 * 1000;
  return Array.from({ length: 6 }).map((_, index) => {
    const ts = weekStartTs + index * DAY_MS;
    const dayKey = toPeriodDateKey(ts);
    const weekday = new Date(ts).getDay();
    return {
      ts,
      key: dayKey,
      lessons: normalizeDayLessons(mergedScheduleData.value[dayKey] || []),
      isWeekend: weekday === 0 || weekday === 6,
      isVacation: isVacationDay(ts),
    };
  });
});

const bellList = computed(() => {
  const raw = Array.isArray(bellsQuery.data.value) ? bellsQuery.data.value : [];
  return raw
    .map((item) => {
      const bells = Array.isArray(item?._bells)
        ? item._bells
        : Array.isArray(item?.bells)
        ? item.bells
        : [];
      const shift = Number(item?._data?.shift ?? item?.shift ?? 0);
      const type = Number(item?._data?.type ?? item?.type ?? 0);

      return {
        ...item,
        _data: {
          ...(item?._data || {}),
          shift,
          type,
        },
        _bells: [...bells].sort((a, b) =>
          String(a?.start_time || "").localeCompare(String(b?.start_time || ""))
        ),
      };
    })
    .filter((item) => item._bells.length && Number(item?._data?.type ?? 0) === 0)
    .sort((a, b) => Number(a?._data?.shift ?? 0) - Number(b?._data?.shift ?? 0));
});

function onDayChange(next) {
  if (!next) return;
  selectedDay.value = next;

  const dayWeek = next?.day?.week;
  const dayWeekStart = toUtcDaySeconds(dayWeek?.start);
  const dayWeekEndRaw = toUtcDaySeconds(dayWeek?.end);
  const dayWeekEnd = dayWeekEndRaw > 0 ? dayWeekEndRaw - 1 : 0;
  const isAnotherWeek =
    dayWeekStart > 0 &&
    dayWeekEnd > 0 &&
    (dayWeekStart !== Number(requestedWeekStartTs.value || 0) ||
      dayWeekEnd !== Number(requestedWeekEndTs.value || 0));

  if (isAnotherWeek) {
    cachedScheduleTypes.value = [];
    requestedWeekStartTs.value = dayWeekStart;
    requestedWeekEndTs.value = dayWeekEnd;
    const weekByStart = weekItems.value.find(
      (item) => toUtcDaySeconds(item?.week?.start) === dayWeekStart
    );
    if (weekByStart) selectedWeek.value = weekByStart;
    nextTick(() => {
      schedulePlansQuery.refetch();
      scheduleMarksQuery.refetch();
    });
  }

  const dayKey = toPeriodDateKey(Number(next?.day?.date || 0));
  const currentType =
    scheduleTypes.value.find(
      (item) => Number(item?.type || 0) === Number(scheduleType.value || 0)
    ) || scheduleTypes.value[0];
  const hasDayData = !!(currentType?.data && currentType.data[dayKey]);
  if (!hasDayData) {
    requestedDayTs.value = toUtcDaySeconds(next?.day?.date);
  }
}

function onWeekChange(next) {
  if (!next) return;
  const week = next?.week && typeof next.week === "object" ? next.week : next;
  if (!week || typeof week !== "object") return;

  selectedWeek.value = next?.week ? next : { title: "", subtitle: "", week };

  const nextStart = toUtcDaySeconds(week?.start);
  const nextEndRaw = toUtcDaySeconds(week?.end);
  const nextEnd = nextEndRaw > 0 ? nextEndRaw - 1 : 0;
  if (!nextStart || !nextEnd) return;

  const firstTs = Number(week?.days?.[0]?.date || week?.start || 0);
  if (firstTs) {
    const nextDay = dayItems.value.find(
      (item) => Number(item?.day?.date || 0) === firstTs
    );
    if (nextDay) selectedDay.value = nextDay;
    requestedDayTs.value = toUtcDaySeconds(firstTs);
  }

  requestedWeekStartTs.value = nextStart;
  requestedWeekEndTs.value = nextEnd;
  cachedScheduleTypes.value = [];
  nextTick(() => {
    scheduleQuery.refetch();
    schedulePlansQuery.refetch();
    scheduleMarksQuery.refetch();
  });
}

function changeScheduleType(type) {
  const next = Number(type || 0);
  if (!next) return;
  scheduleType.value = next;
}

function scheduleDayEntries(scheduleDay) {
  if (Array.isArray(scheduleDay)) return scheduleDay;
  if (!scheduleDay || typeof scheduleDay !== "object") return [];
  return Object.values(scheduleDay);
}

function cloneScheduleData(source) {
  try {
    return JSON.parse(JSON.stringify(source || {}));
  } catch {
    return {};
  }
}

function toPlanRow(plan) {
  return rawRow(plan);
}

function toMarkRow(mark) {
  return rawRow(mark);
}

function ensureLessonArrays(scheduleData) {
  Object.keys(scheduleData || {}).forEach((dayKey) => {
    const entries = scheduleDayEntries(scheduleData[dayKey]);
    entries.forEach((entry) => {
      const lessons = Array.isArray(entry?.lesson) ? entry.lesson : [];
      lessons.forEach((lesson) => {
        if (!Array.isArray(lesson.lplans)) lesson.lplans = [];
        if (!Array.isArray(lesson.mark)) lesson.mark = [];
        if (!Array.isArray(lesson.endDate)) lesson.endDate = [];
      });
    });
  });
}

function attachPlans(scheduleData, plans) {
  const groupedPlans = new Map();
  plans.forEach((plan) => {
    const row = toPlanRow(plan);
    const dayKey = normalizeDateKey(row?.date || plan?.date);
    if (!dayKey) return;
    const subjectId = Number(row?.subject_id || 0);
    const trainingType = Number(row?.training_type || 0);
    const key = `${subjectId}_${trainingType}`;
    if (!groupedPlans.has(dayKey)) groupedPlans.set(dayKey, new Map());
    const bySignature = groupedPlans.get(dayKey);
    if (!bySignature.has(key)) bySignature.set(key, []);
    bySignature.get(key).push(row);
  });

  groupedPlans.forEach((bySignature, dayKey) => {
    const entries = scheduleDayEntries(scheduleData[dayKey]);
    if (!entries.length) return;

    bySignature.forEach((list) => {
      list.sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
    });

    entries.forEach((entry) => {
      const lessons = Array.isArray(entry?.lesson) ? entry.lesson : [];
      lessons.forEach((lesson) => {
        const subjectId = Number(lesson?.subject_id || 0);
        const trainingType = Number(lesson?.training_type || 0);
        const key = `${subjectId}_${trainingType}`;
        const queue = bySignature.get(key);
        if (!queue || !queue.length) return;
        const plan = queue.shift();
        if (!plan) return;

        if (
          !lesson.lplans.find((item) => Number(item?.id || 0) === Number(plan?.id || 0))
        ) {
          lesson.lplans.push(plan);
        }

        if (plan?.secondary && typeof plan.secondary === "object") {
          const secondaryId = Number(plan.secondary?.id || 0);
          if (
            secondaryId > 0 &&
            !lesson.lplans.find((item) => Number(item?.id || 0) === secondaryId)
          ) {
            lesson.lplans.push(plan.secondary);
          }
        }

        const ltasks = Array.isArray(plan?.ltasks) ? plan.ltasks : [];
        ltasks.forEach((task) => {
          const endDate = task?.end_date;
          if (!endDate) return;
          const status = Number(task?.results?.[0]?.status ?? 0);
          const exists = lesson.endDate.find(
            (item) => String(item?.endDate || "") === String(endDate)
          );
          if (!exists) lesson.endDate.push({ endDate, status });
        });
      });
    });
  });
}

function attachMarks(scheduleData, marks) {
  marks.forEach((mark) => {
    const row = toMarkRow(mark);
    const dayKey = normalizeDateKey(row?.date || mark?.date);
    if (!dayKey) return;
    const entries = scheduleDayEntries(scheduleData[dayKey]);
    if (!entries.length) return;

    const subjectId = Number(row?.s_id || 0);
    const lpId = Number(row?.lp_id || 0);
    let matched = false;

    entries.forEach((entry) => {
      const lessons = Array.isArray(entry?.lesson) ? entry.lesson : [];
      lessons.forEach((lesson) => {
        if (Number(lesson?.subject_id || 0) !== subjectId) return;
        const hasPlan = Array.isArray(lesson?.lplans)
          ? lesson.lplans.some((plan) => Number(plan?.id || 0) === lpId)
          : false;
        if (!hasPlan) return;

        if (!lesson.mark.find((item) => Number(item?.id || 0) === Number(row?.id || 0))) {
          lesson.mark.push({
            ...row,
            lplan:
              lesson.lplans.find((plan) => Number(plan?.id || 0) === lpId) ||
              row?.lplan ||
              null,
          });
        }
        matched = true;
      });
    });

    if (matched) return;
    entries.some((entry) => {
      const lessons = Array.isArray(entry?.lesson) ? entry.lesson : [];
      const lesson = lessons.find((item) => Number(item?.subject_id || 0) === subjectId);
      if (!lesson) return false;
      if (!lesson.mark.find((item) => Number(item?.id || 0) === Number(row?.id || 0))) {
        lesson.mark.push(row);
      }
      return true;
    });
  });
}

function mergeScheduleData(baseData, plans, marks) {
  const next = cloneScheduleData(baseData);
  ensureLessonArrays(next);
  attachPlans(next, plans);
  attachMarks(next, marks);
  return next;
}

function goToDay(ts) {
  const targetTs = Number(ts || 0);
  if (!targetTs) return;
  const nextDay = dayItems.value.find(
    (item) => Number(item?.day?.date || 0) === targetTs
  );
  if (!nextDay) return;
  onDayChange(nextDay);
  section.value = "day";
}

function openLesson(lesson) {
  const plans = Array.isArray(lesson?.lplans) ? lesson.lplans : [];
  if (!plans.length) return;
  const firstPlanId = Number(plans[0]?.id || 0);
  if (!firstPlanId) return;
  const firstPlanTheme = String(plans[0]?.theme_name || "").trim();
  const lessonName =
    lesson?.subject?.[lang.value] ||
    lesson?.subject?.[lang.value === "kz" ? "kk" : "ru"] ||
    lesson?.subject?.ru ||
    "";
  const selectedDayMs = Number(selectedDay.value?.day?.date || 0);
  router.push(
    `/task?lessonId=${firstPlanId}&lessonName=${encodeURIComponent(lessonName)}&theme=${encodeURIComponent(firstPlanTheme)}&day=${selectedDayMs}`
  );
}

const isScheduleLoading = computed(
  () =>
    periodsQuery.isFetching.value ||
    scheduleQuery.isFetching.value ||
    schedulePlansQuery.isFetching.value ||
    scheduleMarksQuery.isFetching.value
);
const isBellLoading = computed(
  () => periodsQuery.isFetching.value || bellsQuery.isFetching.value
);
const schedulePageErrors = computed(() =>
  [
    periodsQuery,
    scheduleQuery,
    schedulePlansQuery,
    scheduleMarksQuery,
    bellsQuery,
  ]
    .filter((query) => query.isError?.value)
    .map((query) => query.error?.value)
);

watch(
  () => isScheduleLoading.value || isBellLoading.value,
  (value) => loadingStore.set("schedule-page", value),
  { immediate: true }
);

watch(
  () => [selectedScheduleTitle.value, section.value],
  () => {
    if (section.value === "bell") {
      topbarStore.setTitle(t("child-schedule"));
      return;
    }
    topbarStore.setTitle(selectedScheduleTitle.value);
  },
  { immediate: true }
);

onMounted(() => {
  topbarStore.setTitle(selectedScheduleTitle.value);
  topbarStore.addButton({
    type: "right",
    icon: "history_edu",
    action: () => router.push("/all-tasks"),
  });
  topbarStore.addButton({
    type: "right",
    icon: "loop",
    action: () => {
      periodsQuery.refetch();
      if (section.value === "bell") {
        bellsQuery.refetch();
      } else {
        scheduleQuery.refetch();
        schedulePlansQuery.refetch();
        scheduleMarksQuery.refetch();
      }
    },
  });
});

onBeforeUnmount(() => {
  loadingStore.hide("schedule-page");
});
</script>

<style scoped>
.schedule-page__content {
  margin-top: 20px;
  background: #fff;
  border-radius: 8px;
}

@media screen and (min-width: 768px) {
  .schedule-page__content {
    margin-top: 50px;
    min-height: 700px;
  }
}
</style>
